import { Injectable } from '@angular/core';
import { sanitize } from 'dompurify';

const urlRegex = new RegExp(/(\b(https?):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi);

const allowedTagsIndex: {
  [key: string]: { attributes: Map<string, Set<string>> };
} = {
  span: {
    attributes: new Map([
      ['contenteditable', new Set(['false'])],
      ['class', new Set(['person'])],
    ]),
  },
  div: {
    attributes: new Map(),
  },
  br: {
    attributes: new Map(),
  },
  a: {
    attributes: new Map([['href', null]]),
  },
};

const purifyConfig = {
  ADD_ATTR: ['contenteditable'],
};

function sanitizeElement(element: HTMLElement): Node {
  if (element.nodeType === Node.ELEMENT_NODE) {
    const tagMetadata = allowedTagsIndex[element.tagName.toLowerCase()];

    if (tagMetadata) {
      Array.from(element.attributes).forEach((attribute) => {
        const attrName = attribute.name.toLowerCase();

        if (
          !tagMetadata.attributes.has(attrName) ||
          (tagMetadata.attributes.get(attrName) &&
            !tagMetadata.attributes.get(attrName).has(element.getAttribute(attrName).toLowerCase()))
        ) {
          element.removeAttribute(attrName);
        }
      });

      element.childNodes.forEach((childNode) => {
        const sanitizedNode = sanitizeElement(childNode as HTMLElement);

        if (sanitizedNode !== childNode) {
          element.replaceChild(sanitizedNode, childNode);
        }
      });

      return element;
    }

    return document.createTextNode(element.outerHTML);
  }

  return element;
}

export function sanitizeCommentBody(richText: string): string {
  richText = sanitize(richText, purifyConfig);
  const div = document.createElement('span');
  div.innerHTML = richText;
  const sanitized = sanitizeElement(div);

  if (sanitized.nodeType === Node.TEXT_NODE) {
    return (sanitized as any).textContent;
  } else {
    return (sanitized as HTMLElement).innerHTML;
  }
}

@Injectable({
  providedIn: 'root'
})
export class MessageBodySanitizerService {
  /**
   * Sanitizes comment body string by removing disallowed tag names, attributes, script callbacks, etc.
   * @param richText HTML based rich text
   * @returns sanitized HTML based rich text
   */
  sanitizeCommentBody(richText: string): string {
    richText = sanitize(richText, purifyConfig);
    const div = document.createElement('span');
    div.innerHTML = richText;
    const sanitized = sanitizeElement(div);

    if (sanitized.nodeType === Node.TEXT_NODE) {
      return (sanitized as any).textContent;
    } else {
      return (sanitized as HTMLElement).innerHTML;
    }
  }

  sanitizeAndCreateNodes(pastedText: string): Node[] {
    pastedText = this.sanitizeCommentBody(pastedText);
    return this.handleUrl(pastedText);
  }

  private handleUrl(pastedText: string): Node[] {
    const node = window.document.createElement('p');
    node.innerHTML = pastedText.replace(urlRegex, '<a href="$&">$&</a>');
    return Array.from(node.childNodes);
  }
}
